import { z } from 'zod'

// Worker Schema for contract validation
export const WorkerSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1, 'Worker name is required').max(100, 'Worker name too long'),
  normalizedName: z.string().min(1, 'Normalized name is required'),
  firstSeen: z.string().datetime('First seen must be valid ISO date'),
  lastSeen: z.string().datetime('Last seen must be valid ISO date').nullable(),
  totalDaysWorked: z.number().int().min(0, 'Total days worked must be non-negative'),
  status: z.enum(['active', 'inactive']),
  createdAt: z.string().datetime('Created at must be valid ISO date'),
  updatedAt: z.string().datetime('Updated at must be valid ISO date')
})

// Worker Search Request Schema  
export const WorkerSearchRequestSchema = z.object({
  q: z.string().max(100, 'Search query too long').optional().default('')
})

// Worker Search Response Schema
export const WorkerSearchResponseSchema = z.object({
  workers: z.array(WorkerSchema),
  total: z.number().int().min(0, 'Total must be non-negative')
})

// Worker Create Request Schema
export const WorkerCreateRequestSchema = z.object({
  name: z.string()
    .min(1, 'Worker name is required')
    .max(100, 'Worker name too long')
    .regex(/^[a-zA-Z\s\-'.]+$/, 'Worker name must contain only letters, spaces, hyphens, apostrophes, and periods')
})

// Worker Create Response Schema
export const WorkerCreateResponseSchema = z.object({
  worker: WorkerSchema,
  message: z.string().optional()
})

// Worker Update Request Schema (for future use)
export const WorkerUpdateRequestSchema = z.object({
  name: z.string()
    .min(1, 'Worker name is required')
    .max(100, 'Worker name too long')
    .regex(/^[a-zA-Z\s\-'.]+$/, 'Worker name must contain only letters, spaces, hyphens, apostrophes, and periods')
    .optional(),
  status: z.enum(['active', 'inactive']).optional()
})

// Worker Update Response Schema (for future use)
export const WorkerUpdateResponseSchema = z.object({
  worker: WorkerSchema,
  message: z.string().optional()
})

// Export TypeScript types
export type Worker = z.infer<typeof WorkerSchema>
export type WorkerSearchRequest = z.infer<typeof WorkerSearchRequestSchema>
export type WorkerSearchResponse = z.infer<typeof WorkerSearchResponseSchema>
export type WorkerCreateRequest = z.infer<typeof WorkerCreateRequestSchema>
export type WorkerCreateResponse = z.infer<typeof WorkerCreateResponseSchema>
export type WorkerUpdateRequest = z.infer<typeof WorkerUpdateRequestSchema>
export type WorkerUpdateResponse = z.infer<typeof WorkerUpdateResponseSchema>

// Helper function to normalize worker name
export const normalizeWorkerName = (name: string): string => {
  return name.toLowerCase().trim().replace(/\s+/g, ' ')
}

// Helper function to validate worker name format
export const validateWorkerName = (name: string): { valid: boolean; error?: string } => {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: 'Worker name is required' }
  }
  
  if (name.trim().length < 2) {
    return { valid: false, error: 'Worker name must be at least 2 characters' }
  }
  
  if (name.trim().length > 100) {
    return { valid: false, error: 'Worker name is too long (max 100 characters)' }
  }
  
  const validNamePattern = /^[a-zA-Z\s\-'.]+$/
  if (!validNamePattern.test(name.trim())) {
    return { valid: false, error: 'Worker name must contain only letters, spaces, hyphens, apostrophes, and periods' }
  }
  
  return { valid: true }
}

// Helper function to format worker display name
export const formatWorkerDisplayName = (name: string): string => {
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
} 